import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { onAuthStateChanged, signOut } from "firebase/auth";
import { auth } from "../config/firebase";
import { recordLogin } from "../api/accountApi";

type LogoutFn = () => Promise<void>;

export interface AuthContextValue {
  userId: any;
  setUserId: (userId: any) => void;
  email: any;
  setEmail: (email: any) => void;
  logout: LogoutFn;
  setLogout: (logout: LogoutFn) => void;
  isAuthLoading: boolean;
  /** The account's organizationId from the backend, null if the user hasn't created/joined one yet. */
  accountOrganizationId: string | null;
  setAccountOrganizationId: (organizationId: string | null) => void;
  isAccountLoading: boolean;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [userId, setUserId] = useState<string | null>(null);
  const [email, setEmail] = useState<string | null>(null);
  const [isAuthLoading, setIsAuthLoading] = useState(true);
  const [accountOrganizationId, setAccountOrganizationId] = useState<
    string | null
  >(null);
  const [isAccountLoading, setIsAccountLoading] = useState(true);

  // logout was previously a no-op placeholder that nothing ever replaced
  // via setLogout, so "Log out" never actually signed anyone out.
  const logout = useCallback<LogoutFn>(async () => {
    await signOut(auth);
    localStorage.removeItem("tujud_uid");
    localStorage.removeItem("anonymous_tujud_uid");
    localStorage.removeItem("emailForSignIn");
    localStorage.removeItem("email");
    setUserId(null);
    setEmail(null);
  }, []);
  const setLogout = useCallback((_next: LogoutFn) => {}, []);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setUserId(user?.uid ?? null);
      setEmail(user?.email ?? null);
      setIsAuthLoading(false);

      if (user) {
        setIsAccountLoading(true);
        // Creates the account if missing and stamps last_login_at in one call.
        recordLogin()
          .then((account) => {
            setAccountOrganizationId(account?.organizationId ?? null);
          })
          .catch((error) => {
            console.warn("Failed to ensure backend account exists:", error);
          })
          .finally(() => {
            setIsAccountLoading(false);
          });
      } else {
        setAccountOrganizationId(null);
        setIsAccountLoading(false);
      }
    });
    return unsubscribe;
  }, []);

  const value = useMemo(
    () => ({
      userId,
      setUserId,
      email,
      setEmail,
      logout,
      setLogout,
      isAuthLoading,
      accountOrganizationId,
      setAccountOrganizationId,
      isAccountLoading,
    }),
    [
      userId,
      setUserId,
      email,
      setEmail,
      logout,
      setLogout,
      isAuthLoading,
      accountOrganizationId,
      isAccountLoading,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextValue => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
